<template>
<div class="yin-yang-charts-wrapper">
  <div class="trend-bar-chart-wrapper">
    <div class="select-ctrl-group-wrapper">
      <select-ctrl-group :ids="selectedNames" :options="yinYangFactors" id-prop="name" />
    </div>
    <e-chart ref="trend-chart" @initialized="onInitialized" @destroy="onDestroy" />
  </div>
  <div class="yin-yang-related-chart-wrapper">
    <div class="yin-yang-related-chart-header">
      <span>阴离子</span>
      <span>VS</span>
      <span>阳离子</span>
    </div>
    <div>
      <related-chart
        :base-data="yinYangRelated[0]"
        :related-data="yinYangRelated[1]"
      />
    </div>
  </div>
</div>
</template>

<script>
import EChart from '../Charts/ECharts'
import RelatedChart from './RelatedChart'
import {TEXT_FONT_FAMILY} from './FactorCharts/chartStyle'
import SelectCtrlGroup from '../SelectCtrlGroup/index'

export const YIN_YANG_CONFIG = Object.freeze({
  // Na+
  6107: {
    type: 1,
    weight: 23
  },
  // NH4+
  6108: {
    type: 1,
    weight: 18
  },
  // K+
  6109: {
    type: 1,
    weight: 39
  },
  // Mg2+
  6110: {
    type: 1,
    weight: 12
  },
  // Ca2+
  6111: {
    type: 1,
    weight: 20
  },
  // SO42-
  6104: {
    type: 0,
    weight: 48
  },
  // NO3-
  6103: {
    type: 0,
    weight: 62
  },
  // Cl-
  6101: {
    type: 0,
    weight: 35.5
  },
  // F-
  6100: {
    type: 0,
    weight: 19
  }
})

export const YIN_YANG_FACTOR_IDS = Object.freeze(Object.keys(YIN_YANG_CONFIG))

export default {
  name: 'YinYang',
  components: {SelectCtrlGroup, RelatedChart, EChart},
  props: {
    query: {
      required: true,
      type: Object
    },
    factorMap: {
      required: true,
      type: Object
    },
    data: {
      required: true,
      type: Object
    },
    selectedNames: {
      required: true,
      type: Array
    }
  },
  data: () => ({
    dataTimes: Object.freeze([]),
    trendData: Object.freeze([]),
    yinYangRelated: Object.freeze([])
  }),
  computed: {
    yinYangFactors () {
      let {factorMap} = this
      return YIN_YANG_FACTOR_IDS.reduce((list, factorId) => {
        let factor = factorMap[factorId]

        if (factor) {
          list.push(factor)
        }

        return list
      }, [])
    }
  },
  methods: {
    formatData () {
      let {query, factorMap, data} = this
      let beginTS = new Date(query.beginTime).getTime()
      let endTS = new Date(query.endTime).getTime()
      let timeInterval = query.dataType * 60000
      let dataTimes = []

      for (let i = beginTS; i <= endTS; i += timeInterval) {
        dataTimes.push(new Date(i).format())
      }

      this.dataTimes = dataTimes

      let dataTimesLen = dataTimes.length
      let yinYangRelated = [{
        id: 'anions',
        name: '阴离子',
        unit: 'μg/m3',
        data: dataTimes.map(dataTime => ({
          dataTime,
          value: null
        }))
      }, {
        id: 'cations',
        name: '阳离子',
        unit: 'μg/m3',
        data: dataTimes.map(dataTime => ({
          dataTime,
          value: null
        }))
      }]

      this.trendData = Object.freeze(Object.values(data.data.reduce((map, data) => {
        let {factorId} = data
        let seriesData = map[factorId]

        if (seriesData) {
          let index = (new Date(data.dataTime).getTime() - beginTS) / timeInterval
          let {value} = data

          seriesData.data[index] = value

          let yinYangConfig = YIN_YANG_CONFIG[factorId]
          let calcVal = value / yinYangConfig.weight
          let yinYangData = yinYangRelated[yinYangConfig.type].data[index]
          let yinYangValue = yinYangData.value

          yinYangData.value = Number.isFinite(yinYangValue) ? (yinYangValue + calcVal) : calcVal
        }

        return map
      }, YIN_YANG_FACTOR_IDS.reduce((map, factorId) => {
        let factor = factorMap[factorId]
        map[factorId] = {
          id: factorId,
          name: factor.name,
          type: 'bar',
          stack: 'ion',
          data: new Array(dataTimesLen).fill(null),
          showSymbol: false
        }

        return map
      }, {}))))

      this.yinYangRelated = Object.freeze(yinYangRelated)
    },
    render (isReal) {
      let chartOpt = {
        title: {
          text: this.data.siteName,
          left: 'center',
          textStyle: {
            fontFamily: TEXT_FONT_FAMILY
          }
        },
        toolbox: {
          feature: {
            saveAsImage: {}
          },
          top: -6,
          right: 0
        },
        tooltip: {
          trigger: 'axis',
          appendToBody: true
        },
        legend: {
          top: 27,
          left: 'center',
          type: 'scroll',
          width: '80%',
          selected: this.getSelected()
        },
        grid: {
          top: 75,
          right: 20,
          bottom: 50,
          left: 20,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.dataTimes,
          axisTick: {
            alignWithLabel: true
          }
        },
        yAxis: {
          type: 'value',
          name: 'μg/m3',
          axisLine: {
            show: true
          },
          axisTick: {
            show: true
          }
        },
        dataZoom: [{
          type: 'inside',
          start: this.dataTimes.length <= 72 ? 0 : ((this.dataTimes.length - 72) / this.dataTimes.length * 100).toFixed(0),
          end: 100,
          realtime: true,
          xAxisIndex: 0,
          minSpan: 5
        }, {
          type: 'slider',
          start: this.dataTimes.length <= 72 ? 0 : ((this.dataTimes.length - 72) / this.dataTimes.length * 100).toFixed(0),
          end: 100,
          realtime: true,
          xAxisIndex: 0,
          minSpan: 5,
          // height: 39,
          bottom: 9,
          left: 'center',
          handleIcon: 'M10.7,11.9H9.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4h1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
          handleSize: '70%',
          handleStyle: {
            color: '#fff',
            shadowBlur: 4,
            shadowColor: 'rgba(0, 0, 0, 0.15)',
            shadowOffsetX: 2,
            shadowOffsetY: 2
          },
          // dataBackground: {
          //   areaStyle: '#E4F0FF'
          // }
          fillerColor: 'rgba(228, 240, 255, .6)',
          borderColor: '#E4F0FF'
        }],
        series: this.trendData
      }

      this.$refs['trend-chart'].setOpt(chartOpt, isReal)
    },
    getSelected () {
      let factorIdMap = this.selectedNames.reduce((map, name) => {
        map[name] = true
        return map
      }, {})

      this.yinYangFactors.reduce((map, {name}) => {
        if (!map[name]) {
          map[name] = false
        }

        return map
      }, factorIdMap)

      return factorIdMap
    },
    onLegendSelectChanged (param) {
      this.selected = Object.freeze(JSON.parse(JSON.stringify(param.selected)))
      this.$emit('update:selectedNames', Object.entries(this.selected).filter(selected => selected[1]).map(selected => selected[0]))
      // this.render(true)
    },
    onInitialized (chartObj) {
      this.initialized = true
      chartObj.on('legendselectchanged', this.onLegendSelectChanged)
      this.render()
    },
    onDestroy (chartObj) {
      chartObj.off('legendselectchanged', this.onLegendSelectChanged)
      this.initialized = false
    }
  },
  watch: {
    data: {
      immediate: true,
      handler  (data) {
        this.formatData(data)

        if (this.initialized) {
          this.render()
        }
      }
    },
    selectedNames (selectedNames, old) {
      if (selectedNames !== old) {
        return
      }

      let chartObj = this.$refs['trend-chart']
      let chartOpt = chartObj.getOpt()

      if (!chartOpt) {
        return
      }

      chartObj.setOpt({
        legend: {
          selected: this.getSelected()
        }
      }, true)
    }
  }
}
</script>

<style lang="scss">
.yin-yang-charts-wrapper {
  display: grid;
  grid-template-columns: auto 42%;
  height: 100%;

  > * {
    min: {
      width: 0;
      height: 400px;
    }
  }

  > .trend-bar-chart-wrapper {
    display: grid;
    grid-template-rows: max-content auto;

    > * {
      min: {
        width: 0;
        height: 0;
      }
    }

    > .select-ctrl-group-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  > .yin-yang-related-chart-wrapper {
    display: grid;
    grid-template-rows: 100px auto;

    > * {
      min: {
        width: 0;
        height: 0;
      }
    }

    > .yin-yang-related-chart-header {
      font-size: 22px;
      display: grid;
      grid-template-columns: repeat(3, max-content);
      grid-column-gap: .5em;
      justify-content: center;
      align-items: center;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Data/YinYang.vue